import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, MenuItem, Alert, Stack, Chip,
} from '@mui/material';
import { UploadFile as UploadIcon } from '@mui/icons-material';
import axios from '../api/axios';

// Create a device from an uploaded scan file (our agent JSON or GLPI export):
// the fields are auto-filled from the scan so the user doesn't type everything.
function ImportScanDialog({ open, onClose, onCreated }) {
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [draft, setDraft] = useState(null);
  const [scan, setScan] = useState(null);
  const [name, setName] = useState('');
  const [deviceTypeId, setDeviceTypeId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get('/device-types/').then((r) => setDeviceTypes(r.data.items || r.data || [])).catch(() => {});
    axios.get('/locations/').then((r) => setLocations(r.data.items || r.data || [])).catch(() => {});
    setDraft(null); setScan(null); setName(''); setDeviceTypeId(''); setLocationId(''); setError('');
  }, [open]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const json = JSON.parse(reader.result);
        setScan(json); setError('');
        const r = await axios.post('/devices/scan/preview', json);
        setDraft(r.data);
        setName(r.data.name || '');
      } catch (err) {
        setError('Не удалось разобрать файл: ' + (err.response?.data?.detail || err.message));
        setDraft(null); setScan(null);
      }
    };
    reader.readAsText(file);
  };

  const create = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post('/devices/scan/create', {
        device_type_id: deviceTypeId, location_id: locationId || null, name: name || null, scan,
      });
      onCreated(r.data.device_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Добавить устройство из файла-скана</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Загрузите JSON, созданный агентом (<code>inventory_agent.py --output-file scan.json</code>)
            или экспортом GLPI. Поля заполнятся автоматически — не придётся вводить вручную.
          </Alert>
          <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
            Выбрать файл (.json)
            <input type="file" accept=".json,application/json" hidden onChange={onFile} />
          </Button>
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          {draft && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Распознано из скана:</Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {draft.os && <Chip size="small" label={`ОС: ${draft.os}`} />}
                {draft.ram_total_mb && <Chip size="small" label={`ОЗУ: ${Math.round(draft.ram_total_mb / 1024)} ГБ`} />}
                <Chip size="small" label={`Дисков: ${(draft.disks || []).length}`} />
                <Chip size="small" label={`Сет. карт: ${(draft.nics || []).length}`} />
                {draft.software_count > 0 && <Chip size="small" label={`ПО: ${draft.software_count}`} />}
              </Stack>
              <TextField label="Имя устройства *" value={name} onChange={(e) => setName(e.target.value)}
                fullWidth size="small" sx={{ mb: 2 }} />
              <TextField select label="Тип устройства *" value={deviceTypeId}
                onChange={(e) => setDeviceTypeId(e.target.value)} fullWidth size="small" sx={{ mb: 2 }}>
                <MenuItem value="">— выберите —</MenuItem>
                {deviceTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
              <TextField select label="Локация" value={locationId}
                onChange={(e) => setLocationId(e.target.value)} fullWidth size="small">
                <MenuItem value="">— нет —</MenuItem>
                {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </TextField>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Будут созданы: устройство, сетевые интерфейсы по MAC и отпечаток (ОЗУ/диски/ОС) для мониторинга.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Отмена</Button>
        <Button variant="contained" onClick={create} disabled={busy || !draft || !deviceTypeId || !name}>
          {busy ? 'Создание…' : 'Создать устройство'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ImportScanDialog;
